<template>
  <v-btn
    @click="close"
    variant="outlined"
    class="text-black"
    data-cy="unregisterCancelButton"
    :disabled="isUnregisteringDevice"
    >{{ $t('buttons.cancel') }}</v-btn
  >
  <v-spacer></v-spacer>
  <v-btn
    @click="unregisterConfirm"
    class="bg-warning text-white"
    data-cy="unregisterConfirmButton"
    :loading="isUnregisteringDevice"
    :disabled="isUnregisteringDevice"
    >{{ $t('deviceUnregister.unregisterCard.actionButton') }}</v-btn
  >
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { HTTPStatus } from '@/api'
import { NAMESPACES } from '@/store/types'
import { DEVICE_ACTIONS, DEVICE_GETTERS } from '@/store/devices'
import { SUBSCRIPTIONS_ACTIONS, SUBSCRIPTIONS_GETTERS } from '@/store/subscriptions'

export default defineComponent({
  name: 'DeviceUnregisterActions',
  props: {
    deviceId: String,
    useWebsite: { type: Boolean, default: true }
  },
  emits: {
    close: null,
    unregister: null,
    'device-unregistering': (value: boolean) => typeof value === 'boolean'
  },
  data() {
    return {
      isUnregisteringDevice: false
    }
  },
  computed: {
    model(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    serial(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
        this.deviceId
      )
    },
    hasDataServices(): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.HAS_DATA_SERVICES_SUBSCRIPTION}`
      ](this.serial)
    },
    hasWebsite(): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.HAS_WEBSITE_SUBSCRIPTION}`
      ](this.serial)
    },
    hasWebsiteAndDataServices(): boolean {
      return this.hasWebsite && this.hasDataServices
    },
    onAPIPage(): boolean {
      return this.$route.name === 'api-services'
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    async unregisterDevice() {
      let resp
      let didUnregister = false
      let deleteFromStore = false

      if (this.hasWebsiteAndDataServices) {
        //if you have both types of subscriptions
        //then unsubscribe from the one you want
        resp = await this.$api.patchDeviceSubscription({
          model: this.model,
          serial: this.serial,
          delete: true,
          isExternal: !this.useWebsite
        })
        didUnregister = resp.status == HTTPStatus.NoContent
      } else {
        //unregister the device and flag to delete from Vuex store
        resp = await this.$api.postUnregisterDevice(this.deviceId)
        didUnregister = resp.status == HTTPStatus.NoContent
        if (didUnregister) {
          deleteFromStore = true
        }
      }

      return {
        didUnregister,
        deleteFromStore
      }
    },
    async updateStore(didUnregister: boolean, deleteFromStore: boolean) {
      //if an update is required
      if (didUnregister) {
        if (this.$route.name === 'manage-devices') {
          await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_USER_DEVICES}`)
          await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
        } else {
          await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
        }

        const allDevices = this.$auth.isAuthenticated ? this.$store.state.devices.allDevices : {}
        // if device is to be deleted from store,
        // delete from allDevices to prevent 404 response
        // from subscriptions API
        if (deleteFromStore) {
          delete allDevices[this.deviceId]
        }
        await this.$store.dispatch(
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
          {
            auth: this.$auth,
            api: this.$api,
            devices: allDevices
          }
        )

        // deletion from store is deferred until now
        // to prevent parent component
        // from being removed prematurely
        if (deleteFromStore) {
          this.$store.dispatch(
            `${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.DELETE_DEVICE_BY_ID}`,
            this.deviceId
          )
        }
      }
    },
    async unregisterConfirm() {
      this.isUnregisteringDevice = true
      this.$emit('device-unregistering', true)
      const { didUnregister, deleteFromStore } = await this.unregisterDevice()

      await this.updateStore(didUnregister, deleteFromStore)

      this.isUnregisteringDevice = false
      this.$emit('device-unregistering', false)
      if (didUnregister) {
        this.$emit('unregister')
      }

      this.close()
    }
  }
})
</script>
